import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiError, tokenStore } from "@/lib/api";

/**
 * AuthContext states:
 *   user === null        → still checking
 *   user === false       → not authenticated
 *   user === {object}    → authenticated
 */
const AuthContext = createContext(null);

function pickUser(payload) {
  if (!payload || typeof payload !== "object") return null;
  // Strip tokens from user object kept in React state.
  const { access_token, refresh_token, ...rest } = payload;
  return rest;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    // If no access token locally, skip the /auth/me call to avoid noisy 401s.
    if (!tokenStore.getAccess()) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(pickUser(data?.data) || false);
    } catch (_) {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      const payload = data?.data || {};
      if (payload.access_token) {
        tokenStore.set(payload.access_token, payload.refresh_token);
      }
      setUser(pickUser(payload) || false);
      return { ok: true };
    } catch (e) {
      return {
        ok: false,
        error: formatApiError(e),
      };
    }
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      const d = data?.data || {};
      if (d.access_token) {
        tokenStore.set(d.access_token, d.refresh_token);
      }
      setUser(pickUser(d) || false);
      return { ok: true };
    } catch (e) {
      return {
        ok: false,
        error: formatApiError(e),
      };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {
      /* ignore */
    }
    tokenStore.clear();
    setUser(false);
  }, []);

  const updateProfile = useCallback(async (payload) => {
    try {
      const { data } = await api.patch("/me", payload);
      setUser(pickUser(data?.data) || user);
      return { ok: true };
    } catch (e) {
      return {
        ok: false,
        error: formatApiError(e),
      };
    }
  }, [user]);

  const value = { user, login, register, logout, updateProfile, refresh: fetchMe };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
